import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { inboxSearch as inboxSearchApi } from '../../util/api';
import { addOwnEntities } from '../ManageListingsPage/ManageListingsPage.duck';
import { fetchPickupCodeThunk } from '../../ducks/pickup.duck';

const PICKUP_CODE_PROCESS_NAMES = ['chat-offer-buy', 'default-purchase'];
const PICKUP_CODE_LAST_TRANSITIONS = [
  'transition/confirm-payment',
  'transition/share-pickup-address',
  'transition/customer-activate-pickup-code',
  'transition/capture-at-inspection-expire',
];

const INBOX_PAGE_SIZE = 20;
// Enough recent messages per transaction to find the last counterparty message
// (getLastCounterpartyMessageFromParts may skip the viewer's own trailing messages).
const MESSAGE_PREVIEW_LIMIT = 5;

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const loadDataPayloadCreator = async (
  { params, search },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  try {
    const searchParams = parse(search);
    const page = searchParams.page || 1;
    const processNames = getSupportedProcessesInfo().map(p => p.name);

    const queryParams = {
      processNames,
      include: [
        'listing',
        'listing.images',
        'provider',
        'provider.profileImage',
        'customer',
        'customer.profileImage',
        'booking',
        // Ride the last-message preview along in the same query instead of one
        // messages.query per transaction (was an N+1, up to one call per row).
        'messages',
        'messages.sender',
      ],
      'fields.message': ['content', 'createdAt'],
      limitMessages: MESSAGE_PREVIEW_LIMIT,
      'fields.transaction': [
        'processName',
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
        'payinTotal',
        'payoutTotal',
        'lineItems',
        'protectedData',
        'metadata',
      ],
      'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType', 'state'],
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.metadata',
        'deleted',
        'banned',
      ],
      'fields.image': [
        'variants.square-small',
        'variants.square-small2x',
        'variants.listing-card',
        'variants.listing-card-2x',
      ],
      ...createImageVariantConfig('listing-card', 400, 1),
      ...createImageVariantConfig('listing-card-2x', 800, 1),
      page,
      perPage: INBOX_PAGE_SIZE,
    };

    // Load orders and sales in parallel so the merged inbox shows everything
    const [orderResponse, saleResponse] = await Promise.all([
      sdk.transactions.query({ ...queryParams, only: 'order' }),
      sdk.transactions.query({ ...queryParams, only: 'sale' }),
    ]);

    dispatch(addMarketplaceEntities(orderResponse));
    dispatch(addMarketplaceEntities(saleResponse));

    const orderTxs = orderResponse.data.data;
    const saleTxs = saleResponse.data.data;

    const saleListingIds = Array.from(
      new Set(
        saleTxs
          .map(tx => tx.relationships?.listing?.data?.id?.uuid)
          .filter(Boolean)
      )
    );
    await Promise.all(
      saleListingIds.map(id =>
        sdk.ownListings
          .show({ id })
          .then(res => dispatch(addOwnEntities(res)))
          .catch(() => null)
      )
    );
    const allTxs = [...orderTxs, ...saleTxs];

    // Sort by most recent activity
    allTxs.sort((a, b) => {
      const dateA = new Date(a.attributes.lastTransitionedAt);
      const dateB = new Date(b.attributes.lastTransitionedAt);
      return dateB - dateA;
    });

    // Track role per transaction so the UI knows buying vs selling
    const roleByTxId = {};
    orderTxs.forEach(tx => {
      roleByTxId[tx.id.uuid] = 'customer';
    });
    saleTxs.forEach(tx => {
      roleByTxId[tx.id.uuid] = 'provider';
    });

    // Previews come from the messages included on the two transaction queries
    // above (no per-transaction messages.query). Build previewsByTxId from the
    // included messages, oldest-first so getLastCounterpartyMessageFromParts can
    // walk the thread newest-last.
    const messagesById = {};
    [...(orderResponse.data.included || []), ...(saleResponse.data.included || [])].forEach(e => {
      if (e.type === 'message') messagesById[e.id.uuid] = e;
    });
    const previewsByTxId = allTxs.reduce((acc, tx) => {
      const refs = tx.relationships?.messages?.data || [];
      acc[tx.id.uuid] = refs
        .map(ref => messagesById[ref.id.uuid])
        .filter(Boolean)
        .sort((a, b) => new Date(a.attributes.createdAt) - new Date(b.attributes.createdAt));
      return acc;
    }, {});

    // Buyer-side pickup codes: fan out one BFF read per qualifying order
    // tx so the right-pane "Your pickup code" panel and any other consumers
    // can read straight from Redux without component-level useEffect fetches.
    orderTxs.forEach(tx => {
      const processName = tx?.attributes?.processName;
      const lastTransition = tx?.attributes?.lastTransition;
      if (
        PICKUP_CODE_PROCESS_NAMES.includes(processName) &&
        PICKUP_CODE_LAST_TRANSITIONS.includes(lastTransition)
      ) {
        dispatch(fetchPickupCodeThunk({ transactionId: tx.id.uuid }));
      }
    });

    return {
      transactionRefs: entityRefs(allTxs),
      pagination: null,
      previewsByTxId,
      roleByTxId,
    };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const loadDataThunk = createAsyncThunk('InboxV2Page/loadData', loadDataPayloadCreator);

const searchPayloadCreator = async ({ query, page }, { rejectWithValue }) => {
  try {
    const res = await inboxSearchApi(query, page);
    return { results: res.transactions || [], page, query };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const searchInboxThunk = createAsyncThunk('InboxV2Page/search', searchPayloadCreator);

const inboxV2PageSlice = createSlice({
  name: 'InboxV2Page',
  initialState: {
    fetchInProgress: false,
    fetchError: null,
    pagination: null,
    transactionRefs: [],
    previewsByTxId: {},
    roleByTxId: {},
    searchQuery: '',
    searchResults: null,
    searchPage: 1,
    searchInProgress: false,
    searchError: null,
  },
  reducers: {
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
      if (!action.payload) {
        state.searchResults = null;
        state.searchPage = 1;
        state.searchError = null;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.transactionRefs = action.payload.transactionRefs;
        state.pagination = action.payload.pagination;
        state.previewsByTxId = action.payload.previewsByTxId;
        state.roleByTxId = action.payload.roleByTxId;
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })
      .addCase(searchInboxThunk.pending, state => {
        state.searchInProgress = true;
        state.searchError = null;
      })
      .addCase(searchInboxThunk.fulfilled, (state, action) => {
        state.searchInProgress = false;
        state.searchPage = action.payload.page;
        state.searchResults =
          action.payload.page === 1
            ? action.payload.results
            : [...(state.searchResults || []), ...action.payload.results];
      })
      .addCase(searchInboxThunk.rejected, (state, action) => {
        state.searchInProgress = false;
        state.searchError = action.payload;
      });
  },
});

export const { setSearchQuery } = inboxV2PageSlice.actions;

export default inboxV2PageSlice.reducer;

export const loadData = (params, search) => dispatch => dispatch(loadDataThunk({ params, search }));

export const searchInbox = (query, page = 1) => dispatch =>
  dispatch(searchInboxThunk({ query, page }));
